import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, del, get, post } from '../lib/api.ts'
import { formatDate, formatDateTime } from '../lib/format.ts'
import { useAuth } from '../lib/auth.tsx'
import { PageHeader } from '../components/AppShell.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Skeleton } from '../components/ui.tsx'
import { IconAlert, IconBilling, IconEdit, IconTrash } from '../components/icons.tsx'
import { TIMES_PER_DAY } from '@pharmacia/core'

type Detail = {
  prescription: Record<string, unknown>
  lines: Record<string, unknown>[]
  locked: boolean
  reason: string | null
}

/** One entry per day, so the date is printed once rather than repeated. */
/**
 * The dose as it was written, and what it comes to.
 *
 * "5 × twice = 10" rather than a bare 10: the prescription should read
 * back as the instruction that was given, with the arithmetic shown
 * beside it rather than in place of it.
 */
function doseCell(l: Record<string, unknown>) {
  const qty = Number(l['quantity'])
  const times = Number(l['doses_per_day'] ?? 1)
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2))
  if (times <= 1) return <>{fmt(qty)}</>
  const how = TIMES_PER_DAY.find(t => t.value === times)?.short ?? `${times}×`
  return (
    <>
      {fmt(qty)} <span className="dim">× {how} =</span> <b>{fmt(qty * times)}</b>
    </>
  )
}

function groupByDate(lines: Record<string, unknown>[]): [string, Record<string, unknown>[]][] {
  const byDate = new Map<string, Record<string, unknown>[]>()
  for (const l of lines) {
    if (!l['dose_date']) continue
    const d = String(l['dose_date']).slice(0, 10)
    byDate.set(d, [...(byDate.get(d) ?? []), l])
  }
  return [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b))
}

export function PrescriptionDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = useAuth()
  const [data, setData] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)

  const load = useCallback(() => {
    let cancelled = false
    get<Detail>(`/api/prescriptions/${id}`)
      .then(d => { if (!cancelled) setData(d) })
      .catch((e: ApiError) => { if (!cancelled) toast.fail('Could not load prescription', e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, toast])

  useEffect(() => load(), [load])

  if (loading) {
    return (
      <>
        <PageHeader title="Prescription" />
        <div className="content"><div className="page-max card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
          {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} h={22} />)}
        </div></div></div>
      </>
    )
  }
  if (!data) return null

  const p = data.prescription

  return (
    <>
      <PageHeader title={String(p['number'])}>
        <span className={`badge ${p['status'] === 'sent' ? 'final' : 'draft'}`}>
          {p['status'] === 'sent' ? 'With the counter' : 'Draft'}
        </span>
        {can('prescription:write') && !data.locked ? (
          <>
            {p['status'] !== 'sent' ? (
              <Button variant="primary" loading={sending} onClick={async () => {
                setSending(true)
                try {
                  await post(`/api/prescriptions/${id}/send`, { version: Number(p['version']) })
                  toast.ok('Sent to billing', 'The counter can now raise the bill.')
                  load()
                } catch (e) { toast.fail('Could not send', (e as ApiError).message) }
                finally { setSending(false) }
              }}>
                <IconBilling size={14} /> Send to billing
              </Button>
            ) : null}
            <Button onClick={() => navigate(`/prescriptions/${id}/edit`)}><IconEdit /> Edit</Button>
            <Button variant="danger" onClick={() => setConfirming(true)}><IconTrash /> Delete</Button>
          </>
        ) : null}
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)', maxWidth: 820 }}>
          {data.locked ? (
            <div className="notice" role="alert">
              <span style={{ color: 'var(--danger)', marginTop: 1 }}><IconAlert size={15} /></span>
              <span><strong>Closed to changes.</strong> {data.reason}</span>
            </div>
          ) : p['status'] === 'sent' ? (
            <div className="notice info">
              <span style={{ marginTop: 1 }}><IconBilling size={15} /></span>
              <span>
                Handed to the counter. Editing it withdraws the bill, and you will
                need to send it again.
              </span>
            </div>
          ) : (
            <div className="notice info">
              <span style={{ marginTop: 1 }}><IconBilling size={15} /></span>
              <span>
                Still a draft — the counter cannot see a bill for this yet.
              </span>
            </div>
          )}
          <div className="card">
            <div className="card-body detail-grid">
              <div>
                <span className="k">Patient</span>
                <span className="v">{String(p['patient_name'])}</span>
              </div>
              <div>
                <span className="k">MRN</span>
                <span className="v mono">{String(p['mrn'])}</span>
              </div>
              <div>
                <span className="k">Doctor</span>
                <span className="v">{String(p['doctor_name'])}</span>
              </div>
              <div>
                <span className="k">Written</span>
                <span className="v">{formatDateTime(p['prescribed_at'])}</span>
              </div>
            </div>
          </div>

          {data.lines.some(l => l['dose_date']) ? (
            <div className="card">
              <div className="card-head"><h3>Dosing schedule</h3></div>
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th style={{ width: 160 }}>Date</th><th>Product</th><th>Quantity</th></tr></thead>
                  <tbody>
                    {/* The date is printed once per day rather than repeated
                        on every row, so the shape of the schedule is visible. */}
                    {groupByDate(data.lines).map(([date, rows]) =>
                      rows.map((l, i) => (
                        <tr key={`${date}-${i}`}>
                          <td style={i === 0 ? { fontWeight: 550 } : { color: 'var(--ink-3)' }}>
                            {i === 0 ? formatDate(date) : ''}
                          </td>
                          <td style={{ fontWeight: 550 }}>{String(l['product_name'])}</td>
                          <td>{doseCell(l)}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {data.lines.some(l => !l['dose_date']) ? (
            <div className="card">
              <div className="card-head"><h3>General medicines</h3></div>
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th style={{ width: 40 }}>#</th><th>Product</th><th>Quantity</th></tr></thead>
                  <tbody>
                    {data.lines.filter(l => !l['dose_date']).map((l, i) => (
                      <tr key={`g${i}`}>
                        <td className="dim">{i + 1}</td>
                        <td style={{ fontWeight: 550 }}>{String(l['product_name'])}</td>
                        <td>{doseCell(l)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {p['remarks'] ? (
            <div className="card">
              <div className="card-head"><h3>Doctor's remarks</h3></div>
              <div className="card-body" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {String(p['remarks'])}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {confirming ? (
        <ConfirmDialog
          title={`Delete ${p['number']}?`}
          body="The prescription is kept, not erased — it stops appearing in the list, and the audit trail still records it."
          confirmLabel="Delete" danger
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            try {
              await del(`/api/prescriptions/${id}`)
              toast.ok('Prescription deleted')
              navigate('/prescriptions')
            } catch (e) { toast.fail('Could not delete', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}
